import { SBBox, SBLoading } from "modules/Commons";
import SBEmptyState from "modules/Commons/SBLayouts/SBEmptyState";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IComposedFilter } from "domain/Utils";
import { useSystemFilterContext } from "contexts/SystemFilterContext";
import { IDistributionOrder } from "domain/Inventory/Models/IDistributionOrder";
import { IDistributionOrderSummary } from "domain/Inventory/Models/IDistributionOrderSummary";
import SBComposedFilter from "views/Commons/SBComposedFilter";
import { useDistributionOrderRequester } from "modules/DistributionOrder/hooks/useDistributionOrderRequester";
import DistributionOrderTable from "modules/DistributionOrder/components/DistributionOrderTable";
import DistributionOrderEmptyState from "modules/DistributionOrder/components/DistributionOrderEmptyState";
import DistributionOrderUploaderHelper from "modules/DistributionOrder/components/DistributionOrderUploaderHelper";
import { useStyles } from "./style";

const DEFAULT_PAGE_SIZE = 100;
const SCROLL_THRESHOLD = 600;

const hasDateRange = (filter?: IComposedFilter) =>
	!!filter?.picker?.range?.startDate && !!filter?.picker?.range?.endDate;

export default () => {
	const [items, setItems] = useState<IDistributionOrder[]>([]);
	const [loading, setLoading] = useState(false);
	const [isFetchingMore, setIsFetchingMore] = useState(false);
	const [hasMore, setHasMore] = useState(true);
	const [skip, setSkip] = useState(0);
	const [isImportModalOpen, setIsImportModalOpen] = useState(false);
	const [summary, setSummary] = useState<IDistributionOrderSummary | undefined>();
	const [hasValidFilterApplied, setHasValidFilterApplied] = useState(false);
	const classes = useStyles();
	const tableRef = useRef<any>();
	const { t } = useTranslation();

	const requester = useDistributionOrderRequester();
	const { setFiltersContextState } = useSystemFilterContext();
	const activeFilterRef = useRef<IComposedFilter | undefined>();

	const hasItems = items.length > 0;

	const fetchItems = (currentSkip = 0, append = false) => {
		if (append) setIsFetchingMore(true);
		else setLoading(true);

		requester
			.getAll(currentSkip, DEFAULT_PAGE_SIZE, activeFilterRef.current, true)
			.then((result) => {
				setItems((prev) => append ? [...prev, ...result] : result);
				setSkip(currentSkip + result.length);
				setHasMore(result.length === DEFAULT_PAGE_SIZE);
			})
			.finally(() => {
				setLoading(false);
				setIsFetchingMore(false);
			});
	};

	const fetchCount = (filter?: IComposedFilter) => {
		requester.getCount(filter, true).then(setSummary);
	};

	const handleOnApply = (obj: IComposedFilter) => {
		if (obj.filter) setFiltersContextState(obj.filter);
		else setFiltersContextState([]);
		activeFilterRef.current = obj;

		if (!hasDateRange(obj)) {
			setHasValidFilterApplied(false);
			setItems([]);
			setSummary(undefined);
			return;
		}

		setHasValidFilterApplied(true);
		fetchItems(0);
		fetchCount(obj);
	};

	const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
		const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
		if (
			!isFetchingMore &&
			hasMore &&
			scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD
		) {
			fetchItems(skip, true);
		}
	};

	const handleExport = () => {
		tableRef.current?.downloadCSV();
	};

	return (
		<SBBox className={classes.mainBox}>
			<SBBox display="flex" justifyContent="end">
				<SBComposedFilter.Base onApply={handleOnApply}>
					<SBComposedFilter.Picker />
					<SBComposedFilter.Filter />
					<SBComposedFilter.Applicator />
					<SBComposedFilter.Actions
						actions={[
							{
								icon: { name: "upload" },
								title: "uploadCSV",
								onClick: () => setIsImportModalOpen(true),
							},
							{
								icon: { name: "download" },
								title: "exportCSV",
								onClick: handleExport,
							},
						]}
					/>
				</SBComposedFilter.Base>
			</SBBox>

			<SBBox mt={2}>
				<SBLoading loading={loading}>
					<SBEmptyState
						show={hasItems}
						emptyState={
							<DistributionOrderEmptyState
								title={t(
									hasValidFilterApplied
										? "distributionOrderReceivedNoResultsTitle"
										: "distributionOrderReceivedNeedsDateTitle",
								)}
								subtitle={t(
									hasValidFilterApplied
										? "distributionOrderReceivedNoResultsSubTitle"
										: "distributionOrderReceivedNeedsDateSubTitle",
								)}
							/>
						}
					>
						<DistributionOrderTable
							ref={tableRef}
							title={t("distributionOrderReceivedTableTitle")}
							rows={items}
							summary={summary}
							containerHeight={312}
							onScroll={handleScroll}
						/>
					</SBEmptyState>
				</SBLoading>
			</SBBox>

			<DistributionOrderUploaderHelper
				open={isImportModalOpen}
				handleClose={() => setIsImportModalOpen(false)}
				onSave={() => fetchItems(0)}
			/>
		</SBBox>
	);
};
